import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getUserDetails,
  getUserSubscriptionHistory,
  suspendUser,
  activateUser,
  increaseSubscriptionSessions,
} from "../../services/adminUserService";
import EditUserProfileModal from "./EditUserProfileModal";

const AdminUserDetails = () => {
  const { id } = useParams();

  const [user, setUser] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionIncrement, setSessionIncrement] = useState({});

  // 🔹 Suspension state
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");
  const [showEditProfile, setShowEditProfile] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { data: userData } = await getUserDetails(id);
        setUser(userData);

        const { data: subsData } = await getUserSubscriptionHistory(id);
        setSubscriptions(subsData);

        if (userData.suspended && userData.suspendedReason) {
          setSuspendReason(userData.suspendedReason);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // ================= Suspend User =================
  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      alert("Please enter suspension reason");
      return;
    }

    try {
      const { data } = await suspendUser(id, suspendReason);
      setUser(data);
      setShowSuspendModal(false);
    } catch (error) {
      if (error.response) {
      }
    }
  };

  // ================= Activate User =================
  const handleActivate = async () => {
    try {
      const { data } = await activateUser(id);
      setUser(data);
      setSuspendReason("");
    } catch (error) {
      if (error.response) {
      }
    }
  };

  // ================= Increase Sessions =================
  const handleIncreaseSessions = async (subscriptionId) => {
    const additional = Number(sessionIncrement[subscriptionId]);

    if (!additional || additional <= 0) {
      alert("Enter a valid session count");
      return;
    }

    try {
      const { data } = await increaseSubscriptionSessions(id, subscriptionId, additional);
      setSubscriptions((prev) =>
        prev.map((s) => (s.id === subscriptionId ? data : s))
      );
      setSessionIncrement((prev) => ({ ...prev, [subscriptionId]: "" }));
    } catch (error) {
      if (error.response) {
      }
    }
  };

  if (loading) return <p>Loading user details...</p>;
  if (!user) return <p>User not found</p>;

  return (
    <div style={{ padding: "24px" }}>
      <h2>User Details</h2>

      <div style={{ display: "flex", gap: "20px", marginBottom: "30px" }}>
        {user.picture && (
          <img
            src={user.picture}
            alt="User"
            width={350}
            style={{ borderRadius: "50%" }}
          />
        )}

        <div>
          <p><strong>Name:</strong> {user.name || "-"}</p>
          <p><strong>Email:</strong> {user.email}</p>
          <p><strong>Google ID:</strong> {user.googleId || "-"}</p>
          <p><strong>Verified:</strong> {user.isVerified ? "✅" : "❌"}</p>
          <p><strong>Time Zone:</strong> {user.timeZone}</p>
          <p>
            <strong>Created At:</strong>{" "}
            {user.createdAt ? new Date(user.createdAt).toLocaleString() : "-"}
          </p>
          <p>
            <strong>Last Login:</strong>{" "}
            {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : "-"}
          </p>
          <p>
            <strong>Status:</strong>{" "}
            {user.suspended ? "Suspended" : "Active"}
          </p>
          {user.registrationPlatform && (
            <p><strong>Registration Platform:</strong> {user.registrationPlatform}</p>
          )}

          {user.suspended && user.suspendedReason && (
            <p>
              <strong>Suspension Reason:</strong> {user.suspendedReason}
            </p>
          )}

          <button
            style={{ marginTop: "10px", marginRight: "10px" }}
            onClick={user.suspended ? handleActivate : () => setShowSuspendModal(true)}
          >
            {user.suspended ? "Activate User" : "Suspend User / Edit Reason"}
          </button>
          <button
            style={{ marginTop: "10px" }}
            onClick={() => setShowEditProfile(true)}
          >
            ✏️ Edit Profile
          </button>
        </div>
      </div>

      {showEditProfile && (
        <EditUserProfileModal
          user={user}
          onClose={() => setShowEditProfile(false)}
          onSuccess={(updated) => setUser(prev => ({ ...prev, ...updated }))}
        />
      )}

      {/* ================= Suspension Modal ================= */}
      {showSuspendModal && (
        <div style={overlay}>
          <div style={modal}>
            <h3>{user.suspended ? "Edit Suspension" : "Suspend User"}</h3>

            <textarea
              placeholder="Enter suspension reason..."
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              rows={4}
              style={{ width: "100%", marginBottom: "12px" }}
            />

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => setShowSuspendModal(false)}>Cancel</button>
              <button onClick={handleSuspend}>
                {user.suspended ? "Update Reason" : "Suspend"}
              </button>
            </div>
          </div>
        </div>
      )}

      <h3>Subscription History</h3>

      {subscriptions.length === 0 ? (
        <p>No subscriptions found</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f5f5f5" }}>
              <th style={th}>Plan</th>
              <th style={th}>Created At</th>
              <th style={th}>Total</th>
              <th style={th}>Remaining</th>
              <th style={th}>Status</th>
              <th style={th}>Add Sessions</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.map((sub) => (
              <tr key={sub.id}>
                <td style={td}>{sub.subscriptionPlanName}</td>
                <td style={td}>
                  {sub.createdAt ? new Date(sub.createdAt).toLocaleString() : "-"}
                </td>
                <td style={td}>{sub.totalSessions}</td>
                <td style={td}>{sub.remainingSessions}</td>
                <td style={td}>{sub.status}</td>
                <td style={td}>
                  <input
                    type="number"
                    value={sessionIncrement[sub.id] || ""}
                    onChange={(e) =>
                      setSessionIncrement((prev) => ({
                        ...prev,
                        [sub.id]: e.target.value,
                      }))
                    }
                    style={{ width: "60px", marginRight: "6px" }}
                  />
                  <button onClick={() => handleIncreaseSessions(sub.id)}>
                    Add
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const th = {
  padding: "10px",
  border: "1px solid #ddd",
  textAlign: "left",
};

const td = {
  padding: "10px",
  border: "1px solid #ddd",
};

const overlay = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const modal = {
  background: "#fff",
  padding: "20px",
  borderRadius: "6px",
  width: "400px",
};

export default AdminUserDetails;
